import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { LMS_STUDENT_NAV_ITEMS } from '../../config/lmsStudentNavigation';

export interface LmsStudentSidebarProps {
  onNavigate?: () => void;
  mobile?: boolean;
}

function isNavItemActive(pathname: string, path: string, childPaths?: string[]): boolean {
  if (pathname === path || pathname.startsWith(`${path}/`)) {
    return true;
  }
  if (childPaths?.some((child) => pathname === child || pathname.startsWith(`${child}/`))) {
    return true;
  }
  return false;
}

/**
 * Restricted left sidebar for LMS/SSO students (Tamil Tutor, Communication Tutor, Scenario Conversation).
 */
export const LmsStudentSidebar: React.FC<LmsStudentSidebarProps> = ({
  onNavigate,
  mobile = false,
}) => {
  const location = useLocation();
  const { loading: featureAccessLoading, isSchoolFeatureEnabled } = useFeatureAccess();

  const visibleItems = LMS_STUDENT_NAV_ITEMS.filter((item) => {
    if (featureAccessLoading) return false;
    return isSchoolFeatureEnabled(item.featureKey);
  });

  const handleNavigate = () => {
    if (
      mobile ||
      (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)
    ) {
      onNavigate?.();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {mobile && (
        <div className="flex items-center justify-between p-4 border-b border-app-border dark:border-gray-700 lg:hidden" />
      )}

      <nav
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1 bg-app-surface dark:bg-slate-950"
        aria-label="LMS student navigation"
      >
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive = isNavItemActive(location.pathname, item.path, item.childPaths);

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={handleNavigate}
              className={clsx(
                'group flex items-center gap-4 px-5 py-4 rounded-2xl font-semibold text-sm transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl border-2',
                isActive
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 border-indigo-400 scale-[1.02]'
                  : 'bg-white/70 dark:bg-white/10 backdrop-blur-sm text-slate-700 dark:text-slate-200 hover:bg-white hover:dark:bg-white/20 border-transparent hover:border-indigo-200 dark:hover:border-indigo-700 shadow-md hover:shadow-lg'
              )}
              style={{
                boxShadow: isActive
                  ? '0 10px 25px -5px rgba(99, 102, 241, 0.6), 0 20px 40px -10px rgba(79, 70, 229, 0.3)'
                  : '0 4px 12px rgba(0,0,0,0.08)',
              }}
            >
              <div
                className={clsx(
                  'w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:rotate-6',
                  isActive
                    ? 'bg-white/20 backdrop-blur-sm shadow-white/50'
                    : 'bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 shadow-indigo-200/50'
                )}
              >
                <Icon
                  className={clsx(
                    'w-6 h-6',
                    isActive ? 'text-white drop-shadow-lg' : 'text-indigo-600 dark:text-indigo-300'
                  )}
                />
              </div>

              <span className="tracking-wide leading-tight">{item.label}</span>

              {isActive && (
                <div className="ml-auto w-2 h-2 bg-white rounded-full shadow-lg animate-pulse" />
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};
